import {
  Box,
  DistributionBar,
  Flex,
  formatPercent,
  HStack,
  Text,
  toBigWithDefault,
  type BoxProps,
  type DistributionNamedSection,
} from '@talos/kyoko';
import Big, { type BigSource } from 'big.js';

export interface IWouldSizeQtys {
  cumQty?: BigSource;
  iWouldQty?: BigSource;
  crossingQty?: BigSource;
}

export interface IWouldSizePcts {
  restingPct: Big;
  iWouldPct: Big;
  crossingPct: Big;
}

export const IWouldSizeChart = (props: IWouldSizeQtys & BoxProps) => {
  const { restingPct, iWouldPct, crossingPct } = getIWouldSizePcts(props);

  const sections = getIWouldSizeDistSections({ restingPct, iWouldPct, crossingPct });

  return (
    <Flex flexDirection="column" gap="spacing4" {...props}>
      <HStack justifyContent="space-between">
        <Text color="colorTextSubtle" size="fontSizeSmall">
          Resting <Text color="colors.blue.lighten">{formatPercent(restingPct, 2)}%</Text>
        </Text>
        <Text color="colorTextSubtle" size="fontSizeSmall">
          I Would <Text color="colors.green.lighten">{formatPercent(iWouldPct, 2)}%</Text>
        </Text>
        <Text color="colorTextSubtle" size="fontSizeSmall">
          Crossing <Text color="colors.orange.lighten">{formatPercent(crossingPct, 2)}%</Text>
        </Text>
      </HStack>
      <Box w="100%">
        <DistributionBar sections={sections} showHeaders={false} size="small" />
      </Box>
    </Flex>
  );
};

export function getIWouldSizePcts(sizes: IWouldSizeQtys): IWouldSizePcts {
  const cumQty = toBigWithDefault(sizes.cumQty, 0);
  const iWouldQty = toBigWithDefault(sizes.iWouldQty, 0);
  const crossingQty = toBigWithDefault(sizes.crossingQty, 0);

  // Explicity denote when no fills yet
  if (cumQty.eq(0)) {
    return { restingPct: Big(0), iWouldPct: Big(0), crossingPct: Big(0) };
  }

  const exchangeCrossingQty = crossingQty.sub(iWouldQty);
  const restingQty = cumQty.sub(crossingQty);

  return {
    restingPct: restingQty.div(cumQty),
    iWouldPct: iWouldQty.div(cumQty),
    crossingPct: exchangeCrossingQty.div(cumQty),
  };
}

export function getIWouldSizeDistSections(sizes: IWouldSizePcts): DistributionNamedSection[] {
  const sections: DistributionNamedSection[] = [];
  if (!sizes.restingPct.eq(0)) {
    sections.push({
      id: '1',
      header: null,
      parts: [
        {
          id: '1',
          color: 'var(--colors-blue-lighten)',
          value: sizes.restingPct,
          appearance: 'filled',
        },
      ],
    });
  }
  if (!sizes.iWouldPct.eq(0)) {
    sections.push({
      id: '2',
      header: null,
      parts: [
        {
          id: '2',
          color: 'var(--colors-green-lighten)',
          value: sizes.iWouldPct,
          appearance: 'filled',
        },
      ],
    });
  }
  if (!sizes.crossingPct.eq(0)) {
    sections.push({
      id: '3',
      header: null,
      parts: [
        {
          id: '3',
          color: 'var(--colors-orange-lighten)',
          value: sizes.crossingPct,
          appearance: 'filled',
        },
      ],
    });
  }

  if (sections.length === 0) {
    sections.push({
      id: '1',
      header: null,
      parts: [
        {
          id: '1',
          color: 'var(--colors-gray-090)',
          value: Big(1),
          appearance: 'filled',
        },
      ],
    });
  }

  return sections;
}
